import { Box, TextField, Typography } from '@mui/material';
import { Alert } from '@mui/material';
import useLocalDispatch from 'hooks/useLocalDispatch';
import { fetchPendingEntityStatus } from 'IntegrationModule/thunks';
import { FetchPendingEntityStatusResponse } from 'IntegrationModule/types';
import { FC, useState } from 'react';
import JsonViewer from 'shared/JsonViewer';
import { StyledDefaultPrimaryButton, StyledForm } from 'styles';
import { calculateAlertSeverityByStatusCode } from 'utils';
import { useRef, MouseEventHandler } from 'react';
import { useTenantRealmDropdownsFlow } from 'TenantModule/hooks/useTenantRealmDropdownsFlow';
import { TenantSelector } from 'TenantModule/components/TenantSelector';
import RealmSelector from 'SandboxModule/components/RealmSelector';
import ApiClientManager from 'core/apiClient';

const fetchPendingEntityStatusStateId = 'pendingEntityStatusCard';

type FormattedPendingEntityStatusData = FetchPendingEntityStatusResponse & {
  status: number;
};

export const EntityMappingStatusForm: FC<Record<string, never>> = () => {
  const [entityMappingStatusParams, setEntityMappingStatusParams] = useState<{
    targetServiceId: string;
    entityId: string;
  }>({
    targetServiceId: '',
    entityId: '',
  });
  // Tenant/Realm selection (shared selectors)
  const { selectedTenant, selectedRealm, onSelectTenantRealm, onSelectRealm } =
    useTenantRealmDropdownsFlow();
  const realmDropdownRef = useRef<HTMLDivElement>(null);
  const onTenantSelectorClick: MouseEventHandler<HTMLElement> = (e) => {
    if (e.currentTarget && realmDropdownRef.current) {
      onSelectTenantRealm({
        tenantDropdownTarget: e.currentTarget,
        realmDropdownTarget: realmDropdownRef.current,
      });
    }
  };
  const onRealmSelectorClick: MouseEventHandler<HTMLElement> = (e) => {
    if (selectedTenant) {
      onSelectRealm({ target: e.currentTarget, tenantId: selectedTenant.id });
    }
  };
  const { targetServiceId, entityId } = entityMappingStatusParams;
  const { dispatchLocal, selectLocalData } = useLocalDispatch();

  const onEntityMappingStatusValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = event.target;
    setEntityMappingStatusParams((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit: React.MouseEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();

    if (targetServiceId && entityId) {
      const params = {
        targetServiceId: Number(targetServiceId),
        entityId: Number(entityId),
        tenantName: selectedTenant?.tenantName || undefined,
        realmId: selectedRealm?.realm_id || undefined,
      };
      dispatchLocal(fetchPendingEntityStatus, {
        params,
        stateId: fetchPendingEntityStatusStateId,
        formatSuccessResponse: ({ response }) => {
          return {
            ...response.data,
            status: response.status,
          };
        },
      });
    }
  };

  const { data, isRequesting } = selectLocalData<FormattedPendingEntityStatusData>({
    stateId: fetchPendingEntityStatusStateId,
  });
  const { status, ...entityStatusData } = data || {};

  return (
    <>
      <Typography variant="h6" component="h3" sx={{ fontWeight: 600, fontSize: '1.125rem', mb: 1 }}>
        Get Entity Mapping Status
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
          mb: 2,
        }}
      >
        Check the mapping details of a specific entity
      </Typography>
      <StyledForm onSubmit={handleSubmit}>
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
          }}
        >
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              Organization
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 2,
              }}
            >
              {selectedTenant ? (
                <TenantSelector onClick={onTenantSelectorClick} tenantId={selectedTenant.id} />
              ) : (
                <TenantSelector
                  onClick={onTenantSelectorClick}
                  tenantId={ApiClientManager.getTenantId()}
                />
              )}
              <RealmSelector
                onClick={onRealmSelectorClick}
                realmId={selectedRealm?.realm_id || ''}
                ref={realmDropdownRef}
              />
            </Box>
          </Box>
          <TextField
            label="Target Service ID"
            type="number"
            name="targetServiceId"
            value={targetServiceId}
            onChange={onEntityMappingStatusValueChange}
            required
          />
          <TextField
            label="Entity ID"
            type="number"
            name="entityId"
            value={entityId}
            onChange={onEntityMappingStatusValueChange}
            required
          />
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'stretch',
              gap: 6,
              mt: 'auto',
            }}
          >
            {!isRequesting && status && (
              <Alert severity={calculateAlertSeverityByStatusCode(status)}>
                <JsonViewer collapsed src={entityStatusData} />
              </Alert>
            )}
            <StyledDefaultPrimaryButton variant="contained" type="submit" disabled={isRequesting}>
              Request
            </StyledDefaultPrimaryButton>
          </Box>
        </Box>
      </StyledForm>
    </>
  );
};
